import _ from 'lodash'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import ClearRoundedIcon from '@mui/icons-material/ClearRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import EditRoundedIcon from '@mui/icons-material/EditRounded'

import colors from '@/assets/scss/_themes-vars.module.scss'

import PageContainer from '@/components/ui/containers/PageContainer'
import DeleteConfirmationDialog from '@/components/ui/dialogs/DeleteConfirmationDialog'

import UpdateWaitingRoomForm from './containers/UpdateWaitingRoomForm'
import { useDeleteWaitingRoomMutation } from '@/core/services/waiting-room'
import { getApiErrorMessage } from '@/utils/errors'

export function WaitingRoomPage() {
  const navigate = useNavigate()
  const { waitingRoomId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const [deleteWaitingRoom, { isLoading: isDeleting }] = useDeleteWaitingRoomMutation()

  const [editable, setEditable] = useState<boolean>(false)
  const [openDeleteConfirmationDialog, setOpenDeleteConfirmationDialog] = useState<boolean>(false)

  const handleDeleteWaitingRoom = async () => {
    if (!waitingRoomId) return
    try {
      deleteWaitingRoom(waitingRoomId).unwrap()
      enqueueSnackbar(t('notification.waitingRoom.delete.success'), { variant: 'success' })
      navigate('/super-admin/waiting-rooms')
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('notification.waitingRoom.delete.error')), { variant: 'error' })
    }
  }

  const toggleEditable = () => {
    setEditable((prevValue) => !prevValue)
  }

  const toggleDeleteConfirmationDialog = () => {
    setOpenDeleteConfirmationDialog((prev) => !prev)
  }

  return (
    <PageContainer>
      <Paper>
        <Stack direction="row" alignItems="center" justifyContent="space-between" p={3}>
          <Stack spacing={1} sx={{ flex: 1 }}>
            <Typography variant="h2" component="div" noWrap sx={{ fontSize: { sm: '20px', lg: '24px' }, lineHeight: '28px', fontWeight: 700 }}>
              {t('page.waitingRoom.title')}
            </Typography>
            <Typography
              variant="subtitle1"
              component="div"
              noWrap
              sx={{ color: colors.gray500, fontSize: { sm: '16px', lg: '18px' }, lineHeight: '22px', fontWeight: 400 }}
            >
              {t('page.waitingRoom.subtitle')}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button color="primary" onClick={toggleEditable} startIcon={editable ? <ClearRoundedIcon /> : <EditRoundedIcon />} variant="contained">
              {t(`common:button.${editable ? 'cancel' : 'modify'}.label`)}
            </Button>
            <Button color="error" onClick={toggleDeleteConfirmationDialog} startIcon={<DeleteRoundedIcon />} variant="contained">
              {t('common:button.delete.label')}
            </Button>
          </Stack>
        </Stack>
        <Divider />
        <Box p={3}>
          <UpdateWaitingRoomForm editable={editable} setEditable={setEditable} />
        </Box>
      </Paper>
      <DeleteConfirmationDialog
        isDeleting={isDeleting}
        open={openDeleteConfirmationDialog}
        onClose={toggleDeleteConfirmationDialog}
        onDelete={handleDeleteWaitingRoom}
        title={t('dialog.deleteConfirmation.waitingRoom.title')}
        message={t('dialog.deleteConfirmation.waitingRoom.message')}
      />
    </PageContainer>
  )
}

export default WaitingRoomPage
